import { Component } from '@theme/component';
import {
  fetchConfig,
  formatMoneyFromCents,
  getLpoTirePricesUnitCents,
  getLpoTirePricesUnitCentsForQuantity,
  getProductFormQuantityInput,
  isInputAssociatedWithForm,
  preloadImage,
  onAnimationEnd,
  yieldToMainThread,
} from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/** LPO line item property: show as groups of four with spaces; spaces stripped on add to cart. */
const FLAT_EMBROIDERY_PROPERTY_NAME = 'properties[Flat Embroidery Text]';

/**
 * @param {string} raw
 * @returns {string}
 */
function formatFlatEmbroideryGroupsOf4(raw) {
  const compact = String(raw).replace(/\s/g, '');
  if (!compact) return '';
  const parts = [];
  for (let i = 0; i < compact.length; i += 4) {
    parts.push(compact.slice(i, i + 4));
  }
  return parts.join(' ');
}

/**
 * @param {string} str
 * @param {number} index
 */
function compactCharCountBefore(str, index) {
  return str.slice(0, index).replace(/\s/g, '').length;
}

/**
 * @param {string} str
 * @param {number} count
 */
function indexAfterCompactCount(str, count) {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] !== ' ') {
      seen++;
      if (seen === count) return i + 1;
    }
  }
  return str.length;
}

/**
 * @param {HTMLInputElement} input
 * @param {string} nextValue
 */
function applyFlatEmbroideryFormattedValue(input, nextValue) {
  const prev = input.value;
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? start;
  const startC = compactCharCountBefore(prev, start);
  const endC = compactCharCountBefore(prev, end);
  input.value = nextValue;
  const newStart = indexAfterCompactCount(nextValue, startC);
  const newEnd = indexAfterCompactCount(nextValue, endC);
  input.setSelectionRange(newStart, newEnd);
}

/**
 * LPO often associates line properties with the buy-buttons form via `form="BuyButtons-ProductForm-…"`.
 * Prefer the form that owns `input[name="id"]` so it matches those external inputs.
 * @param {HTMLElement} productFormRoot
 * @returns {HTMLFormElement | null}
 */
function getAddToCartFormForComponent(productFormRoot) {
  const variantInput = productFormRoot.querySelector('input[name="id"]');
  if (variantInput instanceof HTMLInputElement && variantInput.form) {
    return variantInput.form;
  }
  return /** @type {HTMLFormElement | null} */ (
    productFormRoot.querySelector('form[data-type="add-to-cart-form"]') || productFormRoot.querySelector('form')
  );
}

/**
 * `properties[Flat Embroidery Text]` with `.cl-po--input` — descendants, `form="…"` proxy, and document scan.
 * @param {HTMLFormElement} form
 * @param {(input: HTMLInputElement) => void} fn
 */
function forEachFlatEmbroideryLinePropertyInput(form, fn) {
  if (!form?.id) return;
  const seen = new Set();
  const visit = (/** @type {HTMLInputElement} */ el) => {
    if (seen.has(el)) return;
    if (el.name !== FLAT_EMBROIDERY_PROPERTY_NAME || !el.classList.contains('cl-po--input')) return;
    if (!isInputAssociatedWithForm(el, form)) return;
    seen.add(el);
    fn(el);
  };

  for (const el of form.querySelectorAll('input')) {
    if (el instanceof HTMLInputElement) visit(el);
  }
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    for (const el of document.querySelectorAll(`input[form="${CSS.escape(form.id)}"]`)) {
      if (el instanceof HTMLInputElement) visit(el);
    }
  }
  for (const el of document.querySelectorAll('input.cl-po--input')) {
    if (el instanceof HTMLInputElement && el.getAttribute('form') === form.id) visit(el);
  }
}

/**
 * Same association rules as {@link forEachFlatEmbroideryLinePropertyInput} but no `cl-po--input` requirement (submit strip).
 * @param {HTMLFormElement} form
 * @param {(input: HTMLInputElement) => void} fn
 */
function forEachEmbroideryPropertyInputForSubmit(form, fn) {
  if (!form?.id) return;
  const seen = new Set();
  const visit = (/** @type {HTMLInputElement} */ el) => {
    if (seen.has(el)) return;
    if (el.name !== FLAT_EMBROIDERY_PROPERTY_NAME) return;
    if (!isInputAssociatedWithForm(el, form)) return;
    seen.add(el);
    fn(el);
  };

  for (const el of form.querySelectorAll('input')) {
    if (el instanceof HTMLInputElement) visit(el);
  }
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    for (const el of document.querySelectorAll(`input[form="${CSS.escape(form.id)}"]`)) {
      if (el instanceof HTMLInputElement) visit(el);
    }
  }
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {number | undefined} */
  #variantResizeTimeout;

  /** @type {boolean} */
  #syncingLpoPreview = false;

  /** Poll timer for strip + re-apply embroidery spacing while LPO mounts (all PDP loads). */
  /** @type {ReturnType<typeof setInterval> | null} */
  #flatEmbroideryCartEditIntervalId = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate, { signal });
    document.addEventListener('input', this.#onQuantityInputLive, { signal, capture: true });
    document.addEventListener('change', this.#onLpoTirePricesChange, { signal, capture: true });
    document.addEventListener('click', this.#onLpoQuantityBtnClick, { signal });
    document.addEventListener('change', this.#onLpoOptionChange, { signal });
    document.addEventListener('input', this.#onFlatEmbroideryTextInput, { signal });

    // Set add-to-cart button state based on theme minimum quantity (after DOM ready)
    setTimeout(() => {
      this.#updateAddToCartButtonState();
      this.#syncAddToCartLineTotals();
    }, 0);

    this.#syncLpoPreviewOnLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    if (this.#variantResizeTimeout) {
      clearTimeout(this.#variantResizeTimeout);
    }

    if (this.#flatEmbroideryCartEditIntervalId != null) {
      clearInterval(this.#flatEmbroideryCartEditIntervalId);
      this.#flatEmbroideryCartEditIntervalId = null;
    }
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Add to cart label: final total = (qty already in cart for this variant + qty to add) × unit price.
   */
  #syncAddToCartLineTotals() {
    const form = /** @type {HTMLFormElement | null} */ (
      this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('form')
    );
    const quantityInput = getProductFormQuantityInput(form, this);
    const cartQty = parseInt(quantityInput?.getAttribute('data-cart-quantity') || '0', 10) || 0;
    let qtyToAdd = parseInt(quantityInput?.value ?? '', 10);
    if (!Number.isFinite(qtyToAdd) || qtyToAdd < 0) qtyToAdd = 0;
    if (!quantityInput) {
      const qs = /** @type {any} */ (this.refs.quantitySelector);
      if (qs?.getValue) {
        const v = qs.getValue();
        qtyToAdd = parseInt(String(v), 10);
        if (!Number.isFinite(qtyToAdd) || qtyToAdd < 0) qtyToAdd = 0;
      }
      if (qtyToAdd === 0) {
        qtyToAdd = parseInt(this.dataset.quantityDefault ?? '', 10) || 1;
      }
    }
    const totalUnits = cartQty + qtyToAdd;
    const lpoUnitCents = getLpoTirePricesUnitCentsForQuantity(form, totalUnits);

    const containers = this.querySelectorAll('add-to-cart-component');
    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;
      const baseUnit = parseInt(container.dataset.unitPriceCents || '', 10);
      const unit = lpoUnitCents ?? (Number.isFinite(baseUnit) ? baseUnit : NaN);
      if (!Number.isFinite(unit)) continue;
      const currency = container.dataset.currency || 'USD';
      const saleTotal = unit * totalUnits;

      const mount = container.querySelector('[data-atc-line-price]');
      if (!mount) continue;

      mount.innerHTML = `<span class="price">${formatMoneyFromCents(saleTotal, currency)}</span>`;
    }
  }

  /**
   * Updates add-to-cart button disabled state when quantity is below theme minimum.
   */
  #updateAddToCartButtonState() {
    if (!Theme.quantity_min_enabled) return;

    const form = /** @type {HTMLFormElement | null} */ (
      this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('form')
    );
    const quantityInput = getProductFormQuantityInput(form, this);
    const quantity = parseInt(quantityInput?.value ?? '', 10) || parseInt(this.dataset.quantityDefault ?? '', 10) || 1;
    const inputMin = parseInt(quantityInput?.min ?? quantityInput?.getAttribute('min') ?? '', 10);
    const effectiveMin = (inputMin > 0 ? inputMin : Theme.quantity_min) ?? 1;
    const belowMin = quantity < effectiveMin;

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );
    for (const container of allAddToCartContainers) {
      if (belowMin) {
        container.disable();
      } else if (container.dataset.canAddToCart !== 'false') {
        container.enable();
      }
    }
  }

  /**
   * Handles quantity selector updates to disable add-to-cart when below theme min.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#updateAddToCartButtonState();
    this.#syncAddToCartLineTotals();
  };

  /** Live-update ATC price for theme selector and LPO `input[form="…"]` (e.g. `.cl-po--quantity`). */
  #onQuantityInputLive = (/** @type {Event} */ event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement) || t.name !== 'quantity') return;
    const form = /** @type {HTMLFormElement | null} */ (
      this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('form')
    );
    if (!form?.id) return;
    if (t.form !== form && t.getAttribute('form') !== form.id) return;
    this.#syncAddToCartLineTotals();
  };

  /** LPO quantity-discount tiers (`name="_tire-prices"`) — per-unit price comes from `._price`, not variant.price. */
  #onLpoTirePricesChange = (/** @type {Event} */ event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio') return;
    const name = (t.name || '').toLowerCase();
    if (!name.includes('tire-prices') && !name.includes('tier-prices')) return;
    const form = /** @type {HTMLFormElement | null} */ (
      this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('form')
    );
    if (!form || !isInputAssociatedWithForm(t, form)) return;
    // LPO may update quantity/min after this event; sync after paint so totals match tier + qty.
    requestAnimationFrame(() => {
      this.#syncAddToCartLineTotals();
      requestAnimationFrame(() => this.#syncAddToCartLineTotals());
    });
  };

  /**
   * Bridges LPO +/- span buttons to native `input` events so #onQuantityInputLive
   * (and sticky-add-to-cart.js #onDocumentQuantityInput) can sync the ATC price.
   *
   * LPO renders <span class="cl-po--quantity-btn"> elements instead of real buttons.
   * Clicking them updates input.value programmatically — which does NOT fire a native
   * `input` event — so the ATC price never updates. This listener re-fires the event.
   */
  #onLpoQuantityBtnClick = (event) => {
    const btn = /** @type {Element} */ (event.target)?.closest?.('.cl-po--quantity-btn');
    if (!btn) return;
    const wrap = btn.closest('.cl-po--quantity-wrap');
    if (!wrap) return;
    const input = /** @type {HTMLInputElement | null} */ (wrap.querySelector('input[name="quantity"]'));
    if (!input) return;
    // LPO updates input.value synchronously in its own click handler.
    // rAF ensures the value is settled before we notify price-sync listeners.
    requestAnimationFrame(() => {
      input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    });
  };

  /**
   * Fires the same synthetic resize tick that #onVariantUpdate uses, triggered
   * immediately when any LPO option radio is selected (Color, Placement,
   * Embellishment Type, Size, etc.). This lets Splide and other layout-aware
   * components recalculate their dimensions without waiting for the async section
   * fetch that precedes the variant:update event.
   *
   * Detection: LPO marks every option input with class `cl-po--input`.
   */
  #onLpoOptionChange = (/** @type {Event} */ event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (!t.classList.contains('cl-po--input')) return;
    if (!document.body.classList.contains('cs-product') || this.closest('dialog')) return;

    // Only show the loader for variant-affecting inputs (LPO names them with a
    // `v_` prefix, e.g. `v_Color`). Non-variant LPO inputs don't trigger a
    // `variant:update`, so `hideGalleryLoader` would never clear the spinner.
    // Removal is owned by variant-gallery-filter.js, which hides the loader
    // when the new variant image actually loads — so the gallery stays masked
    // until the image is real on screen, not on a fixed timer.
    if (t.name && t.name.startsWith('v_') && !this.#syncingLpoPreview) {
      const galleryWrapper = document.querySelector('.product-information .cl-po--gallery-wrapper');
      if (galleryWrapper) galleryWrapper.classList.add('ft-gallery--loading');
    }

    if (this.#variantResizeTimeout) clearTimeout(this.#variantResizeTimeout);
    this.#variantResizeTimeout = window.setTimeout(() => {
      window.dispatchEvent(
        new UIEvent('resize', {
          view: window,
          bubbles: false,
          cancelable: false,
        })
      );
    }, 75);
  };

  /**
   * On Edit-from-cart landings, fire synthetic input/keyup events on the
   * embroidery text input so LPO's preview canvas renders the restored text.
   * LPO restores values via `_restoreCartItem()` but doesn't dispatch DOM
   * events on the text inputs, so the preview stays blank until the user
   * types. `change` on radios doesn't trigger the preview — only `input`/
   * `keyup` on text-name fields does.
   *
   * Always-on side effects (run on every PDP visit, not just cart-edit):
   * - `#formatFlatEmbroideryOnLoad` re-formats the embroidery field at a
   *   few late timings to catch slow LPO mounts.
   * - `#startFlatEmbroiderySpacingPollOnLoad` polls every 200 ms for ~2 s
   *   to normalize the embroidery value (strips spaces, groups-of-4).
   */
  #syncLpoPreviewOnLoad() {
    if (!document.body.classList.contains('cs-product') || this.closest('dialog')) return;

    [1600, 1900, 3200].forEach((ms) => window.setTimeout(() => this.#formatFlatEmbroideryOnLoad(), ms));
    this.#startFlatEmbroiderySpacingPollOnLoad();

    if (!new URLSearchParams(window.location.search).has('item')) return;

    this.#scheduleLpoPreviewTextSync([500, 1500], 1000, 10);
  }

  /**
   * Schedule synthetic `input`/`keyup` dispatch on every LPO text input and
   * textarea with a value so LPO's preview canvas re-renders the current
   * personalization. Used for both cart-edit landings and variant changes —
   * LPO doesn't re-fire DOM events itself after either, leaving the preview
   * stale.
   *
   * Fires on EVERY tick (no early-out), because variant changes auto-scroll
   * the gallery to a non-preview slide a few hundred ms after the
   * variant:update event. We need to keep firing past the auto-scroll so the
   * destination slide also picks up the personalization.
   *
   * @param {number[]} delays - immediate timeouts (ms) to attempt the sync
   * @param {number} pollMs   - interval period for the recurring fallback
   * @param {number} pollMax  - max ticks for the recurring fallback
   */
  #scheduleLpoPreviewTextSync(delays, pollMs, pollMax) {
    if (!document.body.classList.contains('cs-product') || this.closest('dialog')) return;

    const trySync = () => {
      const inputs = /** @type {NodeListOf<HTMLInputElement | HTMLTextAreaElement>} */ (
        document.querySelectorAll('.cl-po--input[type="text"], textarea.cl-po--input')
      );
      let fired = 0;
      this.#syncingLpoPreview = true;
      inputs.forEach((input) => {
        if (!input.value) return;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
        fired++;
      });
      this.#syncingLpoPreview = false;
      return fired;
    };

    delays.forEach((ms) => window.setTimeout(trySync, ms));

    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts++;
      trySync();
      if (attempts >= pollMax) window.clearInterval(interval);
    }, pollMs);
  }

  /**
   * PDP: normalize Flat Embroidery Text (`cl-po--input` + `form="BuyButtons-ProductForm-…"`) on a short interval
   * so LPO-injected values get strip + groups-of-four spacing (fixes late-mount / cart-return bugs).
   */
  #startFlatEmbroiderySpacingPollOnLoad() {
    if (this.closest('dialog')) return;
    if (!document.body.classList.contains('cs-product')) return;

    if (this.#flatEmbroideryCartEditIntervalId != null) {
      clearInterval(this.#flatEmbroideryCartEditIntervalId);
      this.#flatEmbroideryCartEditIntervalId = null;
    }

    const TICK_MS = 200;
    const TICK_COUNT = 10;

    this.#normalizeFlatEmbroideryFieldForCartEdit();

    let ticks = 0;
    this.#flatEmbroideryCartEditIntervalId = window.setInterval(() => {
      ticks += 1;
      this.#normalizeFlatEmbroideryFieldForCartEdit();
      if (ticks >= TICK_COUNT - 1 && this.#flatEmbroideryCartEditIntervalId != null) {
        clearInterval(this.#flatEmbroideryCartEditIntervalId);
        this.#flatEmbroideryCartEditIntervalId = null;
      }
    }, TICK_MS);
  }

  /** Strip spaces then apply groups-of-4 spacing for `properties[Flat Embroidery Text]`. */
  #normalizeFlatEmbroideryFieldForCartEdit() {
    if (!document.body.classList.contains('cs-product') || this.closest('dialog')) return;
    const form = getAddToCartFormForComponent(this);
    if (!form?.id) return;

    forEachFlatEmbroideryLinePropertyInput(form, (el) => {
      const stripped = el.value.replace(/\s/g, '').trim();
      const next = stripped === '' ? '' : formatFlatEmbroideryGroupsOf4(stripped);
      if (next !== el.value) el.value = next;
    });
  }

  /** Format embroidery field when LPO injects it (e.g. return from cart). */
  #formatFlatEmbroideryOnLoad() {
    if (!document.body.classList.contains('cs-product') || this.closest('dialog')) return;
    const form = getAddToCartFormForComponent(this);
    if (!form?.id) return;

    forEachFlatEmbroideryLinePropertyInput(form, (el) => {
      const next = formatFlatEmbroideryGroupsOf4(el.value);
      if (next !== el.value) el.value = next;
    });
  }

  /**
   * Live format: space after every 4 non-space characters (input is normalized).
   * Spaces are removed again on submit so the cart line stores compact text.
   * @param {Event} event
   */
  #onFlatEmbroideryTextInput = (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.name !== FLAT_EMBROIDERY_PROPERTY_NAME || !t.classList.contains('cl-po--input')) return;
    if (!document.body.classList.contains('cs-product') || this.closest('dialog')) return;

    const form = getAddToCartFormForComponent(this);
    if (!form || !isInputAssociatedWithForm(t, form)) return;

    const next = formatFlatEmbroideryGroupsOf4(t.value);
    if (next === t.value) return;
    applyFlatEmbroideryFormattedValue(t, next);
  };

  /**
   * @param {HTMLFormElement} form
   */
  #stripFlatEmbroiderySpacesForSubmit(form) {
    if (!form) return;
    forEachEmbroideryPropertyInputForSubmit(form, (el) => {
      el.value = el.value.replace(/\s/g, '').trim();
    });
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
    this.#updateAddToCartButtonState();
    this.#syncAddToCartLineTotals();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = getAddToCartFormForComponent(this);

    if (!form) throw new Error('Product form element missing');

    this.#stripFlatEmbroiderySpacesForSubmit(form);

    // Use quantity from our quantity-selector-component to avoid picking up app block inputs (e.g. Live Product Options)
    const quantityInput =
      this.refs.quantitySelector?.refs?.quantityInput ||
      form.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = parseInt(quantityInput?.value ?? '', 10) || parseInt(this.dataset.quantityDefault ?? '', 10) || 1;

    const inputMin = parseInt(quantityInput?.min ?? quantityInput?.getAttribute('min') ?? '', 10);
    const effectiveMin = inputMin > 0 ? inputMin : (Theme.quantity_min ?? 1);

    if (Theme.quantity_min_enabled && quantity < effectiveMin) {
      const errorTemplate = this.dataset.quantityErrorMin || '';
      const errorMessage = errorTemplate.replace('{{ minimum }}', String(effectiveMin));
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          addToCartTextError.appendChild(document.createTextNode(errorMessage));
        }
        this.#setLiveRegionText(errorMessage);
      }
      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        if (addToCartTextError) addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
      return;
    }

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();
          this.#syncAddToCartLineTotals();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Products with selling plans (subscriptions/volume pricing) report variant.available=false until
    // a plan is selected. Keep the button enabled so users can add to cart after selecting options.
    const hasSellingPlans = parseInt(this.dataset.hasSellingPlans || '0', 10) > 0;
    const shouldDisable =
      event.detail.resource == null ||
      (event.detail.resource.available === false && !hasSellingPlans);

    if (shouldDisable) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (shouldDisable) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const resource = /** @type {{ featured_media?: { preview_image?: { src?: string } }; price?: number }} */ (
        event.detail.resource
      );
      const productVariantMedia = resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
      const acc = this.refs.addToCartButtonContainer;
      if (acc && typeof resource.price === 'number') {
        acc.dataset.unitPriceCents = String(resource.price);
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    this.#syncAddToCartLineTotals();

    // After variant morphing, some layouts need a resize tick to recalc dimensions.
    // Only do this on PDP (template = product) and not inside dialogs (quick add).
    if (document.body.classList.contains('cs-product') && !this.closest('dialog')) {
      if (this.#variantResizeTimeout) clearTimeout(this.#variantResizeTimeout);
      this.#variantResizeTimeout = window.setTimeout(() => {
        window.dispatchEvent(
          new UIEvent('resize', {
            view: window,
            bubbles: false,
            cancelable: false,
          })
        );
      }, 75);

      // Re-sync LPO preview canvas after variant change. LPO re-renders its
      // option UI but doesn't re-fire DOM events on the embroidery text input,
      // so the preview canvas can stay blank for the new variant. Keep firing
      // through the auto-scroll window: variant-gallery-filter.js scrolls to
      // a non-preview slide at ~200 ms and ~700 ms — we need ticks AFTER that
      // so the destination slide also picks up the personalization.
      this.#scheduleLpoPreviewTextSync([300, 800, 1500, 2200], 800, 8);
    }

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
    this.#syncAddToCartLineTotals();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
